import { useDispatch, useSelector } from 'react-redux';
import { useMixpanel } from 'react-mixpanel-browser';
import React, { useMemo } from 'react';

import { ANALYTICS_SET_REPORTS, ANALYTICS_SORT, ANALYTICS_SET_COLUMN_FILTERS } from '../../../store/reducers/analytics';
import { AnalyticsReportType, exportReport } from '../../../store/actions/analytics/export-reports';
import { rlrsReportColumns } from './reportsColumnNames';
import { reportBusinessActionToMixpanel } from '../../../utils/reportBusinessActionToMixpanel';
import { REPORT_TYPES } from './consts';
import { useVisibleColumnsToggler } from '../useVisibleColumnsToggler';
import { getInitialReportState } from '../../../utils/analytics';
import { EmptyPage } from '../../pages/Analytics/EmptyPage';
import { ExportReportPopupToggler } from '../../pages/Reports';
import { FilterSeparator } from '../../filters/FilterSeparator';
import { RlrsTableRow } from './RlrsTableRow';
import { ReportPagination } from './ReportPagination';
import { SendReportNotificationDialog } from './SendReportNotificationDialog';
import { TableHeader } from '../TableHeader';
import { TableWrapper } from '../TableWrapper';
import { getCountryNameFromRow } from './utils';
import { ReportSearch } from './ReportSearch';
import { AnalyticsToggler, TOGGLER_STYLES } from '../../pages/Analytics/AnalyticsToggler';
import { removeHiddenColumns } from './reportsColumnNames/utils';
import { AnalyticsSpinner } from '../../common/Spinner/AnalyticsSpinner';
import { InfoTitle } from '../../common/InfoTooltip';
import { InfoIcon } from '../../icons/InfoIcon';

import styles from './ReportTable.module.scss';

const FULL_SCREEN_COLSPAN = 14;

export function RlrsTable() {
  const dispatch = useDispatch();
  const mixpanel = useMixpanel();
  const { id: supplierId } = useSelector((state) => state.role.supplier);
  const {
    countries,
    retailerNamesByIds,
    currentCurrency,
    ui: {
      loading: { [REPORT_TYPES.RLRS]: isLoading },
    },
    reports: {
      [REPORT_TYPES.RLRS]: { canLoadData },
    },
  } = useSelector((state) => state.analytics);
  const availableColumns = removeHiddenColumns(rlrsReportColumns);
  const { visibleColumns, data, sort, search, filters } = useSelector(
    (state) => state.analytics.reports[REPORT_TYPES.RLRS],
  );
  const rlrWasProcessed = filters?.rlr_was_processed;
  const { column: sortedBy, direction: sortDirection } = sort;

  const { columnsDisplay, widget: visibleColumnsToggler } = useVisibleColumnsToggler({
    allColumns: availableColumns,
    visibleColumns,
    setVisibleColumns: (visibleColumns) =>
      dispatch({
        type: ANALYTICS_SET_REPORTS,
        payload: { reportType: REPORT_TYPES.RLRS, data: { visibleColumns } },
      }),
  });

  const onExport = ({ onlySelectedColumns }) => {
    reportBusinessActionToMixpanel(mixpanel, 'return-notifications-report-export', { supplierId });
    dispatch(
      exportReport({
        reportType: AnalyticsReportType.RLRS,
        onlySelectedColumns,
      }),
    );
  };

  const uiData = useMemo(() => {
    return (data ?? []).map((row) => ({
      ...row,
      retailerName: retailerNamesByIds?.[row.retailerId] ?? '',
      currency: currentCurrency,
      countryName: getCountryNameFromRow(countries, row.countryCode),
    }));
  }, [data, retailerNamesByIds, currentCurrency, countries]);

  const renderTableContent = (uiData, visibleColumns) => {
    if (uiData.length > 0) {
      return uiData.map((row) => (
        <RlrsTableRow key={`${row.retailerId}-${row.cymbioOrderId}-${row.cymbioVariantId}`} row={row} />
      ));
    }

    return (
      <tr>
        <td colSpan={FULL_SCREEN_COLSPAN}>
          <EmptyPage />
        </td>
      </tr>
    );
  };

  return (
    <div className={styles.reportTableWrapper}>
      <SendReportNotificationDialog reportType="Return Notifications" />
      <div className={styles.tableHeaderOptions}>
        <ReportSearch
          value={search}
          onChange={(value) =>
            dispatch({
              type: ANALYTICS_SET_REPORTS,
              payload: { reportType: REPORT_TYPES.RLRS, data: { search: value } },
            })
          }
          placeholder="Search by RA number or Retailer order ID"
        />
        <div className={styles.togglerWithLabelWrapper}>
          <div className={styles.togglerLabel}>
            Open
            <InfoTitle content={'The status indicates if a full refund was issued to the retailer'}>
              <div className={styles.infoIconWrapper}>
                <InfoIcon />
              </div>
            </InfoTitle>
          </div>
          <AnalyticsToggler
            style={TOGGLER_STYLES.Thin}
            selected={rlrWasProcessed}
            onChange={(rlrWasProcessed) => {
              dispatch({
                type: ANALYTICS_SET_COLUMN_FILTERS,
                payload: {
                  reportType: REPORT_TYPES.RLRS,
                  filters: { rlr_was_processed: rlrWasProcessed },
                },
              });
            }}
            options={[
              { value: false, label: 'Yes' },
              { value: true, label: 'No' },
            ]}
          />
          <FilterSeparator />
        </div>
        {visibleColumnsToggler}
        <ExportReportPopupToggler
          title="Export return notifications report"
          onExport={onExport}
          disabled={!data?.length}
        />
      </div>

      <TableWrapper hasExpandedRow={false} className={styles.reportTable}>
        <TableHeader
          columns={columnsDisplay}
          columnsData={availableColumns}
          sort={{
            sortedBy: sortedBy,
            direction: sortDirection,
            initialColumn: getInitialReportState([], REPORT_TYPES.RLRS).sort.column,
            onSortBy: (column, direction) => {
              dispatch({
                type: ANALYTICS_SORT,
                payload: { reportType: REPORT_TYPES.RLRS, column, direction },
              });
            },
          }}
        />

        <tbody>
          {isLoading || !canLoadData ? (
            <tr>
              <td colSpan={FULL_SCREEN_COLSPAN} style={{ height: 300 }}>
                <AnalyticsSpinner />
              </td>
            </tr>
          ) : (
            renderTableContent(uiData, visibleColumns)
          )}
        </tbody>
      </TableWrapper>

      <ReportPagination reportType={REPORT_TYPES.RLRS} />
    </div>
  );
}
